import { useState } from "react";
import { Input } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import { DatePicker } from "../../../../components/common/date-picker";
import { Switch } from "../../../../components/common/switch";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { Text } from "../../../../components/common/typography";
import { PromotionProductPicker } from "./promotion-product-picker";
import { useLoyaltyPromotionMutations } from "../../hooks/useLoyaltyPromotions";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import {
  LoyaltyPromotionDtoScopeEnum,
  type CreateLoyaltyPromotionDto,
  type LoyaltyPromotionDto,
} from "../../../../api";

interface LoyaltyPromotionFormProps {
  promotion?: LoyaltyPromotionDto; // present → edit, absent → create
  onSaved: () => void;
  onCancel: () => void;
}

const toDateInput = (v: string | null | undefined): string => (v ? v.slice(0, 10) : "");

type Scope = CreateLoyaltyPromotionDto["scope"];

export function LoyaltyPromotionForm({ promotion, onSaved, onCancel }: LoyaltyPromotionFormProps) {
  const { create, update } = useLoyaltyPromotionMutations();
  const [name, setName] = useState(promotion?.name ?? "");
  const [multiplier, setMultiplier] = useState(String(promotion?.multiplier ?? 2));
  const [scope, setScope] = useState<Scope>(promotion?.scope ?? LoyaltyPromotionDtoScopeEnum.All);
  const [selected, setSelected] = useState<Map<number, string>>(
    () => new Map((promotion?.productIds ?? []).map((id) => [id, `Product #${id}`])),
  );
  const [start, setStart] = useState(toDateInput(promotion?.startsAt));
  const [end, setEnd] = useState(toDateInput(promotion?.endsAt));
  const [enabled, setEnabled] = useState(promotion?.enabled ?? true);
  const [saving, setSaving] = useState(false);

  const isProducts = scope === LoyaltyPromotionDtoScopeEnum.Products;
  const mult = Number(multiplier);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return showErrorToast("Give the promotion a name.");
    if (!Number.isFinite(mult) || mult < 1) return showErrorToast("Multiplier must be at least 1.");
    if (isProducts && selected.size === 0)
      return showErrorToast("Select at least one product, or switch to all products.");

    const dto: CreateLoyaltyPromotionDto = {
      name: name.trim(),
      multiplier: mult,
      scope,
      productIds: isProducts ? [...selected.keys()] : [],
      enabled,
      startsAt: start || null,
      endsAt: end || null,
    };

    setSaving(true);
    try {
      if (promotion) await update(promotion.id, dto);
      else await create(dto);
      showSuccessToast(promotion ? "Promotion updated." : "Promotion created.");
      onSaved();
    } catch {
      showErrorToast("Failed to save promotion.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5 text-left">
      <Input
        label="Name"
        name="name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Summer double points"
        required
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Input
          label="Multiplier"
          type="number"
          step="0.1"
          min="1"
          name="multiplier"
          value={multiplier}
          onChange={(e) => setMultiplier(e.target.value)}
          helperText="2 = double points"
        />
        {/* size="lg" matches the default <Input> height so the three controls align. */}
        <DatePicker label="Starts" size="lg" value={start} onChange={setStart} />
        <DatePicker label="Ends" size="lg" value={end} min={start || undefined} onChange={setEnd} />
      </div>

      <div className="flex flex-col gap-2">
        <Text variant="bodySmMedium" tone="dark">Applies to</Text>
        <SegmentedControl
          value={scope}
          onChange={(v) => setScope(v as Scope)}
          options={[
            { value: LoyaltyPromotionDtoScopeEnum.All, label: "All products" },
            { value: LoyaltyPromotionDtoScopeEnum.Products, label: "Selected products" },
          ]}
          aria-label="Promotion scope"
        />
      </div>

      {isProducts && <PromotionProductPicker selected={selected} onChange={setSelected} />}

      <div className="flex items-center justify-between rounded-xl border border-borderDefault px-4 py-3">
        <div className="flex flex-col">
          <Text variant="bodySmMedium" tone="dark">Enabled</Text>
          <Text variant="bodyXs" tone="muted">Pause without losing the dates.</Text>
        </div>
        <Switch checked={enabled} onChange={setEnabled} aria-label="Enabled" />
      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" size="md" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="md" disabled={saving}>
          {saving ? "Saving…" : promotion ? "Save changes" : "Create promotion"}
        </Button>
      </div>
    </form>
  );
}
